import { SimpleDashboardConfig } from '../dashboard/components/Dashboard/Dashboard';

/**
 * Encode a dashboard configuration to base64 for use in URLs
 */
export function encodeDashboardConfig(config: SimpleDashboardConfig): string {
  return btoa(JSON.stringify(config));
}

/**
 * Decode a base64 dashboard configuration from URL
 */
export function decodeDashboardConfig(encoded: string): SimpleDashboardConfig {
  try {
    const decoded = atob(encoded)
      .replace('```dashboard', '')
      .replace('```', '');

    const config = JSON.parse(decoded) as SimpleDashboardConfig;

    // Validate the config structure
    if (!config.sections || !Array.isArray(config.sections)) {
      throw new Error(
        'Invalid dashboard configuration: missing or invalid sections array',
      );
    }

    return config;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Failed to decode dashboard configuration: ${error.message}`,
      );
    }
    throw new Error('Failed to decode dashboard configuration');
  }
}

/**
 * Create a dashboard URL with encoded configuration
 */
export function createDashboardUrl(config: SimpleDashboardConfig): string {
  const encoded = encodeDashboardConfig(config);
  return `/dashboard/?content=${encoded}`;
}

/**
 * Validate a dashboard configuration
 */
export function validateDashboardConfig(config: SimpleDashboardConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!config.sections || !Array.isArray(config.sections)) {
    errors.push('Missing or invalid sections array');
  } else {
    config.sections.forEach((section, index) => {
      if (!section.widgets || !Array.isArray(section.widgets)) {
        errors.push(`Section ${index}: Missing or invalid widgets array`);
      } else {
        section.widgets.forEach((widget, widgetIndex) => {
          if (!widget.component) {
            errors.push(
              `Section ${index}, Widget ${widgetIndex}: Missing component type`,
            );
          }
          if (!widget.props || typeof widget.props !== 'object') {
            errors.push(
              `Section ${index}, Widget ${widgetIndex}: Missing or invalid props`,
            );
          }
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
